import { useEffect, useState } from 'react';
import { AlertOctagon, FileJson, RefreshCw } from 'lucide-react';
import { getModelRequestDetail, getModelRequestsByRunId } from '../lib/api';
import type { ModelHttpRequestDetail, ModelHttpRequestSummary } from '../types';
import { StatusPill } from './StatusPill';
import { ModelRequestDetailView } from './ModelRequestDetailView';

export function TraceModelRequestsPanel({ runId }: { runId: string }) {
  const [items, setItems] = useState<readonly ModelHttpRequestSummary[]>([]);
  const [selectedId, setSelectedId] = useState<number>();
  const [detail, setDetail] = useState<ModelHttpRequestDetail>();
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(undefined);
      setDetail(undefined);
      try {
        const response = await getModelRequestsByRunId(runId, controller.signal);
        const sorted = [...response.items].sort((a, b) => a.id - b.id);
        setItems(sorted);
        const selected = sorted.at(-1);
        setSelectedId(selected?.id);
        if (selected) setDetail(await getModelRequestDetail(selected.id, controller.signal));
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === 'AbortError') return;
        setError(nextError instanceof Error ? nextError.message : '无法读取关联模型请求');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    void load();
    return () => controller.abort();
  }, [runId]);

  const select = async (item: ModelHttpRequestSummary) => {
    setSelectedId(item.id);
    setDetail(undefined);
    setDetailLoading(true);
    setError(undefined);
    try {
      setDetail(await getModelRequestDetail(item.id));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : '请求详情读取失败');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) return <div className="empty-state trace-http-state"><RefreshCw className="spin" size={24} /><strong>正在关联发包/回包…</strong><p>按 runId 精确读取 Go 实时请求环。</p></div>;
  if (error && !detail) return <div className="empty-state empty-state--error trace-http-state"><AlertOctagon size={25} /><strong>HTTP 包体读取失败</strong><p>{error}</p></div>;
  if (!items.length) return <div className="empty-state trace-http-state"><FileJson size={25} /><strong>没有关联请求</strong><p>实时内存环中未找到 runId={runId}；服务重启会清空历史。</p></div>;

  return <div className="trace-http-panel">
    <div className="request-rounds" aria-label="模型请求轮次">
      <div><strong>关联模型请求</strong><span>{items.length} 轮 · 默认显示最后一轮</span></div>
      <div>{items.map((item, index) => <button className={selectedId === item.id ? 'request-round--active' : ''} onClick={() => void select(item)} key={item.id}><span>Round {index + 1}</span><code>#{item.id}</code><StatusPill status={item.status} compact /></button>)}</div>
    </div>
    {error && <div className="banner-error" role="alert"><AlertOctagon size={16} /><span>{error}</span></div>}
    {detailLoading ? <div className="empty-state trace-http-state"><RefreshCw className="spin" size={24} /><strong>正在切换请求轮次…</strong></div> : detail && <ModelRequestDetailView detail={detail} />}
  </div>;
}
